import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { getAllowedProductIds } from "settings/account/components/TagsManager/requests/productAdapters";
import {
  addAssignments,
  listAssignments,
  removeAssignment,
} from "../requests/request";
import {
  getNotificationGroupAdapterForProduct,
  listMergedNotificationServices,
  lookupNotificationResourceType,
} from "../requests/notificationGroupProductAdapters";
import {
  listResourcesOfType,
  type ResourceListItem,
} from "../requests/resourceListers";
import {
  DraftResourceAssignment,
  GroupAssignment,
  NotificationGroup,
  RESOURCE_TYPE_LABELS,
} from "../types";
import {
  type TagLinkSelectOption,
  tagLinkLabelToIdMap,
} from "settings/account/components/TagsManager/tagLinkSelectOptions";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";

interface Props {
  /** When null, the picker keeps assignments in memory only (create wizard). */
  group: NotificationGroup | null;
  draftAssignments?: DraftResourceAssignment[];
  onDraftAssignmentsChange?: (rows: DraftResourceAssignment[]) => void;
  /** Fires whenever the assignment list changes (for stepper gating). */
  onAssignmentsCountChange?: (count: number) => void;
  /** Linked tag drives assignments — user edits them in Tags Manager. */
  readOnly?: boolean;
  /** Wizard only: manual resource picker vs mirror from Tags Manager. */
  assignmentMode?: "manual" | "tag";
  onAssignmentModeChange?: (mode: "manual" | "tag") => void;
  linkedTagId?: string | null;
  onLinkedTagChange?: (id: string | null) => void | Promise<void>;
  tagLinkSelectOptions?: TagLinkSelectOption[];
  tagsLoading?: boolean;
  tagsLoadError?: string | null;
  /** True while linked-tag resources are syncing (Tags Manager fetch or edit hydration). */
  linkedTagResourcesLoading?: boolean;
}

const composeKey = (resourceType: string, resourceId: string) =>
  `${resourceType}::${resourceId}`;

type ResourceLookup = Record<string, Record<string, ResourceListItem>>;

const draftRowKey = (d: DraftResourceAssignment) =>
  `${d.resourceType}::${d.resourceId}`;

const GroupAssignmentsPicker: React.FC<Props> = ({
  group,
  draftAssignments = [],
  onDraftAssignmentsChange,
  onAssignmentsCountChange,
  readOnly = false,
  assignmentMode = "manual",
  onAssignmentModeChange,
  linkedTagId = null,
  onLinkedTagChange,
  tagLinkSelectOptions = [],
  tagsLoading = false,
  tagsLoadError = null,
  linkedTagResourcesLoading = false,
}) => {
  const isDraftMode = group === null;
  const api = useAxiosAuth0();
  const tenantId = useAppSelector((s) => s.selection.tenantId);
  const isViewingAllCustomers = useAppSelector(
    (s) => s.selection.tenantId === s.selection.partnerId
  );
  const productList = useAppSelector((s) => s.userProfile.ProductList);
  const allowedProductIds = useMemo(
    () => getAllowedProductIds(productList),
    [productList]
  );

  const mergedServices = useMemo(
    () => listMergedNotificationServices(allowedProductIds),
    [allowedProductIds]
  );
  const [activeResourceType, setActiveResourceType] = useState<string>(
    mergedServices[0]?.type ?? ""
  );

  const activeAdapter = useMemo(() => {
    const entry = lookupNotificationResourceType(activeResourceType);
    return (
      entry?.product ??
      getNotificationGroupAdapterForProduct(null)
    );
  }, [activeResourceType]);

  useEffect(() => {
    if (
      activeResourceType &&
      !mergedServices.find((s) => s.type === activeResourceType)
    ) {
      setActiveResourceType(mergedServices[0]?.type ?? "");
    }
  }, [mergedServices, activeResourceType]);

  const serviceDropdownItems = useMemo(
    () => mergedServices.map((svc) => `${svc.label} — ${svc.productLabel}`),
    [mergedServices]
  );

  const serviceLabelToType = useMemo(() => {
    const map = new Map<string, string>();
    for (const svc of mergedServices) {
      map.set(`${svc.label} — ${svc.productLabel}`, svc.type);
    }
    return map;
  }, [mergedServices]);

  const activeServiceLabel = useMemo(() => {
    const svc = mergedServices.find((s) => s.type === activeResourceType);
    return svc ? `${svc.label} — ${svc.productLabel}` : undefined;
  }, [mergedServices, activeResourceType]);

  const tagDropdownItems = useMemo(
    () => tagLinkSelectOptions.map((t) => t.label),
    [tagLinkSelectOptions]
  );

  const tagLabelToId = useMemo(
    () => tagLinkLabelToIdMap(tagLinkSelectOptions),
    [tagLinkSelectOptions]
  );

  const linkedTagLabel = useMemo(() => {
    if (!linkedTagId) return undefined;
    return tagLinkSelectOptions.find((t) => t.id === linkedTagId)?.label;
  }, [tagLinkSelectOptions, linkedTagId]);

  const tagDropdownDefaultLabel = useMemo(() => {
    if (tagsLoading) return "Loading tags…";
    if (tagsLoadError) return "Could not load tags";
    if (tagLinkSelectOptions.length === 0) {
      return "No tags available — create one in Tags Manager";
    }
    return "Choose a tag to mirror resources from Tags Manager";
  }, [tagsLoading, tagsLoadError, tagLinkSelectOptions.length]);

  const showWizardSourceControls = !!onAssignmentModeChange;
  const manualResourcesUi =
    assignmentMode === "manual" && !linkedTagId;

  const [persistedAssignments, setPersistedAssignments] = useState<
    GroupAssignment[]
  >([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);

  const assignments: GroupAssignment[] = useMemo(() => {
    if (isDraftMode) {
      return draftAssignments.map((d) => ({
        id: `draft::${draftRowKey(d)}`,
        groupId: "",
        tenantId: 0,
        resourceType: d.resourceType,
        resourceId: d.resourceId,
        eventTypes: d.eventTypes,
        createdAt: "",
        resourceDisplayHint: d.resourceDisplayHint ?? null,
        resourceSubtitle: d.resourceSubtitle ?? null,
      }));
    }
    return persistedAssignments;
  }, [isDraftMode, draftAssignments, persistedAssignments]);

  const [resources, setResources] = useState<ResourceListItem[]>([]);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceError, setResourceError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set());
  const [lookup, setLookup] = useState<ResourceLookup>({});
  const [pendingDetachIds, setPendingDetachIds] = useState<Set<string>>(
    new Set()
  );

  const loadAssignments = useCallback(async () => {
    if (!tenantId || !group) return;
    setAssignmentsLoading(true);
    setAssignmentsError(null);
    try {
      const res = await listAssignments(api, tenantId, group.id);
      const rows = res.data.data;
      setPersistedAssignments(rows);
      onAssignmentsCountChange?.(rows.length);
    } catch {
      setAssignmentsError("Couldn't load resource assignments.");
      setPersistedAssignments([]);
      onAssignmentsCountChange?.(0);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [api, tenantId, group, onAssignmentsCountChange]);

  useEffect(() => {
    if (isDraftMode) {
      onAssignmentsCountChange?.(draftAssignments.length);
      return;
    }
    void loadAssignments();
  }, [
    isDraftMode,
    draftAssignments.length,
    group?.id,
    loadAssignments,
    onAssignmentsCountChange,
  ]);

  useEffect(() => {
    if (readOnly) return;
    if (!tenantId || assignments.length === 0) return;
    const uniqueTypes = Array.from(
      new Set(assignments.map((a) => a.resourceType))
    );
    const missing = uniqueTypes.filter((t) => lookup[t] === undefined);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const updates: ResourceLookup = {};
      await Promise.all(
        missing.map(async (type) => {
          try {
            const items = await listResourcesOfType(api, tenantId, type);
            const byId: Record<string, ResourceListItem> = {};
            for (const it of items) byId[String(it.id)] = it;
            updates[type] = byId;
          } catch {
            updates[type] = {};
          }
        })
      );
      if (!cancelled) {
        setLookup((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, tenantId, assignments, lookup, readOnly]);

  useEffect(() => {
    if (readOnly || !manualResourcesUi) {
      setResources([]);
      setResourceLoading(false);
      setResourceError(null);
      return;
    }
    if (!activeResourceType || !tenantId) {
      setResources([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setResourceLoading(true);
      setResourceError(null);
      try {
        const items = await activeAdapter.listResources(
          api,
          tenantId,
          activeResourceType
        );
        if (!cancelled) {
          setResources(items);
          setPickedIds(new Set());
          setSearch("");
        }
      } catch {
        if (!cancelled) {
          setResources([]);
          setResourceError("Couldn't load resources for this service.");
        }
      } finally {
        if (!cancelled) setResourceLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [
    activeAdapter,
    api,
    tenantId,
    activeResourceType,
    readOnly,
    manualResourcesUi,
  ]);

  const attachedKeys = useMemo(
    () =>
      new Set(
        assignments.map((a) => composeKey(a.resourceType, a.resourceId))
      ),
    [assignments]
  );

  const filteredResources = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return resources;
    return resources.filter((r) =>
      [
        r.label,
        r.subscriptionId,
        r.id,
        r.subtitle,
        r.status,
        r.customer?.name,
        r.customer?.chargebeeCustomerId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [resources, search]);

  const togglePicked = useCallback((resourceId: string) => {
    setPickedIds((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  }, []);

  const handleAttachSelection = useCallback(async () => {
    if (readOnly) return;
    if (!pickedIds.size || !activeResourceType) return;
    if (!isDraftMode && (!tenantId || !group)) return;
    const additions = Array.from(pickedIds)
      .filter((id) => !attachedKeys.has(composeKey(activeResourceType, id)))
      .map((resourceId) => ({
        resourceType: activeResourceType,
        resourceId,
        eventTypes: null as string[] | null,
      }));
    if (additions.length === 0) return;
    setAssignmentsError(null);
    if (isDraftMode && onDraftAssignmentsChange) {
      const next = [...draftAssignments];
      const seen = new Set(next.map(draftRowKey));
      for (const a of additions) {
        const k = draftRowKey(a);
        if (!seen.has(k)) {
          seen.add(k);
          next.push(a);
        }
      }
      onDraftAssignmentsChange(next);
      onAssignmentsCountChange?.(next.length);
      setPickedIds(new Set());
      return;
    }
    if (!tenantId || !group) return;
    try {
      await addAssignments(api, tenantId, group.id, additions);
      await loadAssignments();
      setPickedIds(new Set());
    } catch {
      setAssignmentsError("Couldn't attach resources.");
    }
  }, [
    tenantId,
    pickedIds,
    attachedKeys,
    activeResourceType,
    api,
    group,
    loadAssignments,
    isDraftMode,
    draftAssignments,
    onDraftAssignmentsChange,
    onAssignmentsCountChange,
    readOnly,
  ]);

  const handleRemoveOne = useCallback(
    async (assignmentId: string) => {
      if (readOnly) return;
      setAssignmentsError(null);
      if (isDraftMode && onDraftAssignmentsChange) {
        const target = assignments.find((a) => a.id === assignmentId);
        if (!target) return;
        const key = draftRowKey({
          resourceType: target.resourceType,
          resourceId: target.resourceId,
          eventTypes: target.eventTypes,
        });
        const next = draftAssignments.filter((d) => draftRowKey(d) !== key);
        onDraftAssignmentsChange(next);
        onAssignmentsCountChange?.(next.length);
        return;
      }
      if (!tenantId || !group) return;
      setPendingDetachIds((prev) => {
        const next = new Set(prev);
        next.add(assignmentId);
        return next;
      });
      try {
        await removeAssignment(api, tenantId, group.id, assignmentId);
        setPersistedAssignments((prev) => {
          const next = prev.filter((a) => a.id !== assignmentId);
          onAssignmentsCountChange?.(next.length);
          return next;
        });
      } catch {
        setAssignmentsError("Failed to detach resource.");
      } finally {
        setPendingDetachIds((prev) => {
          const next = new Set(prev);
          next.delete(assignmentId);
          return next;
        });
      }
    },
    [
      api,
      tenantId,
      group,
      onAssignmentsCountChange,
      isDraftMode,
      onDraftAssignmentsChange,
      draftAssignments,
      assignments,
      readOnly,
    ]
  );

  const handleRemoveResourceTab = useCallback(
    async (resourceType: string) => {
      if (readOnly) return;
      const toRemove = assignments.filter((a) => a.resourceType === resourceType);
      if (toRemove.length === 0) return;
      setAssignmentsError(null);
      if (isDraftMode && onDraftAssignmentsChange) {
        const next = draftAssignments.filter((d) => d.resourceType !== resourceType);
        onDraftAssignmentsChange(next);
        onAssignmentsCountChange?.(next.length);
        return;
      }
      if (!tenantId || !group) return;
      try {
        await Promise.all(
          toRemove.map((a) =>
            removeAssignment(api, tenantId, group.id, a.id)
          )
        );
        await loadAssignments();
      } catch {
        setAssignmentsError("Failed to detach resources.");
      }
    },
    [
      assignments,
      tenantId,
      api,
      group,
      loadAssignments,
      isDraftMode,
      onDraftAssignmentsChange,
      draftAssignments,
      onAssignmentsCountChange,
      readOnly,
    ]
  );

  const assignmentsByResourceType = useMemo(() => {
    const grouped = new Map<string, GroupAssignment[]>();
    for (const a of assignments) {
      const arr = grouped.get(a.resourceType) ?? [];
      arr.push(a);
      grouped.set(a.resourceType, arr);
    }
    return Array.from(grouped.entries());
  }, [assignments]);

  const [activeDraftTab, setActiveDraftTab] = useState<string | null>(null);

  useEffect(() => {
    if (assignmentsByResourceType.length === 0) {
      setActiveDraftTab(null);
      return;
    }
    const stillExists = assignmentsByResourceType.find(
      ([type]) => type === activeDraftTab
    );
    if (!stillExists) {
      setActiveDraftTab(assignmentsByResourceType[0][0]);
    }
  }, [assignmentsByResourceType, activeDraftTab]);

  const handleTabClick = useCallback((resourceType: string) => {
    setActiveDraftTab(resourceType);
    setActiveResourceType(resourceType);
  }, []);

  const activeTabItems = useMemo(() => {
    if (!activeDraftTab) return [];
    return assignments.filter((a) => a.resourceType === activeDraftTab);
  }, [assignments, activeDraftTab]);

  const distinctResourceTypeCount = assignmentsByResourceType.length;

  const showSelectedResourcesSkeleton =
    (assignmentsLoading && assignments.length === 0) ||
    (!!linkedTagId && linkedTagResourcesLoading && assignments.length === 0);

  const activeMergedService = useMemo(
    () => mergedServices.find((s) => s.type === activeResourceType),
    [mergedServices, activeResourceType]
  );

  const tabLabel = (resourceType: string) =>
    lookupNotificationResourceType(resourceType)?.service.label ??
    RESOURCE_TYPE_LABELS[resourceType] ??
    resourceType;

  const renderAssignmentLine = (a: GroupAssignment) => {
    const hinted = a.resourceDisplayHint?.trim();
    const lookupReady =
      !!hinted || lookup[a.resourceType] !== undefined;

    if (!lookupReady) {
      return (
        <>
          <div className="flex items-center gap-2">
            <span
              className="h-4 w-40 rounded bg-semantic-bg-secondary animate-pulse"
              aria-hidden
            />
          </div>
          <span
            className="h-3 w-56 rounded bg-semantic-bg-secondary animate-pulse mt-1.5"
            aria-hidden
          />
        </>
      );
    }

    const item = lookup[a.resourceType]?.[a.resourceId];
    const name =
      hinted ||
      item?.label ||
      `Resource #${a.resourceId} (not found)`;

    return (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-semantic-text-primary truncate">
            {name}
          </span>
        </div>
        <span className="text-xs text-semantic-text-secondary truncate mt-0.5">
          {(() => {
            const secondaryId =
              item?.subscriptionId?.trim() ||
              item?.subtitle?.trim() ||
              a.resourceSubtitle?.trim() ||
              a.resourceId;
            return (
              <>
                <span className="font-mono">{secondaryId}</span>
                {item?.status ? (
                  <>
                    {" · "}
                    <span>{item.status}</span>
                  </>
                ) : null}
              </>
            );
          })()}
        </span>
      </>
    );
  };

  return (
    <div className="space-y-5">
      <div className="space-y-5">
        <div className="w-full md:max-w-3xl">
          <h3 className="text-md font-semibold text-semantic-text-primary">
            Resources
          </h3>
          <p className="text-xs text-semantic-text-secondary mt-1 md:max-w-2xl">
            {readOnly ? (
              <>
                These resources come from the linked tag (read-only). Edit the
                tag in Tags Manager if you need to change them.
              </>
            ) : showWizardSourceControls ? (
              <>
                Attach resources to this group, then choose how to assign them.
              </>
            ) : (
              <>
                Attach Kontrol Edge and Reflector resources to this group.
                Pick a service, then add resources below (same flow as Tags
                Manager).
              </>
            )}
          </p>
        </div>

        {showWizardSourceControls && (
          <div className="space-y-3">
            <span className="text-sm font-medium text-semantic-text-primary">
              Resource assignment
            </span>
            <div
              role="radiogroup"
              aria-label="Resource assignment source"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl"
            >
              {(
                [
                  {
                    value: "manual" as const,
                    title: "Manual configuration",
                    description:
                      "Pick a service and attach resources one by one.",
                    disabled: false,
                  },
                  {
                    value: "tag" as const,
                    title: "Linked tag",
                    description:
                      "Mirror resource assignments from a tag until you save. Add or change recipients on the Recipients step.",
                    disabled: false,
                  },
                ]
              ).map((opt) => {
                const selected = assignmentMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-disabled={opt.disabled}
                    onClick={() => !opt.disabled && onAssignmentModeChange?.(opt.value)}
                    className={`text-left px-3 py-2 rounded-md border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-semantic-border-brand-secondary ${
                      opt.disabled
                        ? "border-semantic-border-secondary bg-semantic-bg-secondary cursor-not-allowed opacity-60"
                        : selected
                        ? "border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary"
                        : "border-semantic-border-secondary bg-semantic-bg-primary hover:border-semantic-border-primary"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        aria-hidden
                        className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          opt.disabled
                            ? "border-semantic-border-secondary"
                            : selected
                            ? "border-semantic-border-brand-secondary"
                            : "border-semantic-border-secondary"
                        }`}
                      >
                        {selected && !opt.disabled && (
                          <span className="w-1.5 h-1.5 rounded-full bg-semantic-bg-brand-bold-secondary" />
                        )}
                      </span>
                      <div className="min-w-0 leading-tight">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs font-semibold ${
                              opt.disabled
                                ? "text-semantic-text-secondary"
                                : selected
                                ? "text-semantic-text-brand"
                                : "text-semantic-text-primary"
                            }`}
                          >
                            {opt.title}
                          </span>
                          {opt.disabled && (
                            <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-semantic-bg-secondary text-semantic-text-secondary border border-semantic-border-secondary">
                              Coming soon
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-semantic-text-secondary mt-0.5">
                          {opt.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {assignmentMode === "tag" && (
              <div className="max-w-md w-full pt-1">
                <label className="block text-sm font-medium text-semantic-text-secondary mb-2">
                  Tag
                </label>
                <GenericDropdown
                  className="[&_button]:overflow-hidden [&_button_span]:truncate [&_button_span]:block"
                  items={tagDropdownItems}
                  value={linkedTagLabel}
                  defaultLabel={tagDropdownDefaultLabel}
                  onSelect={(label) => {
                    const id = tagLabelToId.get(label);
                    void onLinkedTagChange?.(id ?? null);
                  }}
                  disabled={
                    tagsLoading ||
                    !!tagsLoadError ||
                    tagLinkSelectOptions.length === 0
                  }
                  loading={tagsLoading}
                  size="sm"
                  widthClass="w-full"
                  maxWidthClass=""
                  minWidthClass=""
                  dropdownMaxHeightClass="max-h-[240px]"
                />
                {tagsLoadError && (
                  <p className="mt-1.5 text-xs text-semantic-text-danger">
                    {tagsLoadError}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {assignmentsError && (
        <p className="text-sm text-semantic-text-danger">{assignmentsError}</p>
      )}

      {readOnly && (
        <div className="rounded-md border border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary px-3 py-2 text-xs text-semantic-text-brand-primary">
          View-only: resource rows stay in sync with the tag until you leave this
          screen, change the tag above, or clear the link.
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-semantic-text-primary">
            Selected resources
          </h4>
          <span className="text-xs text-semantic-text-secondary">
            {showSelectedResourcesSkeleton
              ? "Loading…"
              : `${assignments.length} resource${assignments.length === 1 ? "" : "s"} · ${distinctResourceTypeCount} service${distinctResourceTypeCount === 1 ? "" : "s"}`}
          </span>
        </div>
        {showSelectedResourcesSkeleton ? (
          <div
            className="border border-semantic-border-secondary rounded-md bg-semantic-bg-primary px-4 py-5"
            aria-busy="true"
            aria-label="Loading selected resources"
          >
            <SkeletonTableRows rows={4} cols={1} rowHeight={14} animated />
          </div>
        ) : assignments.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-semantic-border-secondary rounded-md">
            <p className="text-sm text-semantic-text-secondary">
              {readOnly
                ? "No resources on the linked tag (or unsupported service types)."
                : showWizardSourceControls &&
                  assignmentMode === "tag" &&
                  !linkedTagId
                ? "Select a tag above to mirror resources from Tags Manager."
                : manualResourcesUi
                ? "No resources attached yet. Add some from the picker below."
                : "Switch to manual configuration or select a tag to add resources."}
            </p>
          </div>
        ) : (
          <div className="border border-semantic-border-secondary rounded-md overflow-hidden bg-semantic-bg-primary">
            <div className="flex items-stretch overflow-x-auto custom-scrollbar bg-semantic-bg-secondary border-b border-semantic-border-secondary">
              {assignmentsByResourceType.map(([resourceType, items]) => {
                const svcLabel = tabLabel(resourceType);
                const isActive = resourceType === activeDraftTab;
                return (
                  <div
                    key={resourceType}
                    className={`flex items-center gap-1.5 border-r border-semantic-border-secondary ${
                      isActive
                        ? "bg-semantic-bg-primary border-b-2 border-b-semantic-border-brand-secondary -mb-px"
                        : "hover:bg-semantic-state-active"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleTabClick(resourceType)}
                      className={`px-3 py-2 text-sm font-medium ${
                        isActive
                          ? "text-semantic-text-brand"
                          : "text-semantic-text-primary"
                      }`}
                    >
                      {svcLabel}
                      <span className="ml-1.5 text-xs text-semantic-text-secondary">
                        ({items.length})
                      </span>
                    </button>
                    {!readOnly && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleRemoveResourceTab(resourceType);
                      }}
                      aria-label={`Remove all ${svcLabel}`}
                      title={`Remove all ${svcLabel}`}
                      className="mr-2 w-5 h-5 flex items-center justify-center rounded text-semantic-text-secondary hover:text-semantic-text-error hover:bg-semantic-state-active text-sm leading-none"
                    >
                      ×
                    </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="max-h-[200px] overflow-y-auto custom-scrollbar divide-y divide-semantic-border-secondary">
              {activeTabItems.length === 0 ? (
                <div className="px-3 py-6 text-sm text-semantic-text-secondary text-center">
                  No resources in this service.
                </div>
              ) : (
                activeTabItems.map((a) => {
                  const detaching = pendingDetachIds.has(a.id);
                  return (
                    <div
                      key={a.id}
                      className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${
                        detaching ? "opacity-50" : ""
                      }`}
                    >
                      <div className="min-w-0 flex-1 flex flex-col">
                        {renderAssignmentLine(a)}
                      </div>
                      {!readOnly && (
                      <GenericButton
                        label={detaching ? "Detaching" : "Remove"}
                        variant="secondary"
                        size="sm"
                        loading={detaching}
                        disabled={detaching}
                        onClick={() => void handleRemoveOne(a.id)}
                      />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {manualResourcesUi && !readOnly && (
      <div className="border border-semantic-border-primary rounded-md p-4 bg-semantic-bg-secondary">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h4 className="text-sm font-semibold text-semantic-text-primary">
            {assignments.length === 0
              ? "Add resources"
              : "Add another service"}
          </h4>
          {activeMergedService && (
            <span className="text-xs text-semantic-text-secondary truncate max-w-[min(100%,14rem)]">
              {activeMergedService.productLabel}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-semantic-text-secondary mb-1">
              Service
            </label>
            <GenericDropdown
              fixedPosition={true}
              dropdownMaxHeightClass="max-h-48"
              items={serviceDropdownItems}
              value={activeServiceLabel}
              defaultLabel={
                mergedServices.length === 0
                  ? "No services available"
                  : "Select a service"
              }
              onSelect={(label) => {
                const type = serviceLabelToType.get(label);
                if (type) setActiveResourceType(type);
              }}
              disabled={mergedServices.length === 0}
              size="md"
              widthClass="w-full"
              minWidthClass="min-w-0 md:min-w-[320px]"
              maxWidthClass="max-w-full"
            />
          </div>

          <GenericInput
            label="Filter resources"
            value={search}
            onChange={setSearch}
            placeholder={isViewingAllCustomers ? "Search by customer, name, subscription id, or status…" : "Search by name, subscription id, or status…"}
            type="search"
          />

          <GenericButton
            label={
              pickedIds.size === 0
                ? "Add"
                : `Add ${pickedIds.size} ${
                    activeMergedService?.singularLabel.toLowerCase() ?? "item"
                  }${pickedIds.size === 1 ? "" : "s"}`
            }
            variant="brand"
            size="md"
            disabled={
              pickedIds.size === 0 || !activeResourceType
            }
            onClick={() => void handleAttachSelection()}
          />
        </div>

        <div className="mt-3 border border-semantic-border-secondary rounded-md overflow-hidden bg-semantic-bg-primary">
          <div className={`grid ${isViewingAllCustomers ? "grid-cols-[36px_minmax(100px,1fr)_1fr_140px_120px]" : "grid-cols-[36px_1fr_140px_120px]"} gap-3 px-3 py-2 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wide bg-semantic-bg-secondary border-b border-semantic-border-secondary`}>
            <span></span>
            {isViewingAllCustomers && <span>Customer</span>}
            <span>Name</span>
            <span>Subscription ID</span>
            <span>Status</span>
          </div>
          <div className="max-h-[220px] overflow-y-auto custom-scrollbar divide-y divide-semantic-border-secondary">
            {resourceLoading ? (
              <div className="px-3 py-4" aria-busy="true" aria-label="Loading resources">
                <SkeletonTableRows
                  rows={6}
                  cols={1}
                  rowHeight={14}
                  animated
                  rowGapClassName="space-y-3"
                />
              </div>
            ) : resourceError ? (
              <div className="px-3 py-6 text-sm text-semantic-text-danger">
                {resourceError}
              </div>
            ) : filteredResources.length === 0 ? (
              <div className="px-3 py-8 text-sm text-semantic-text-secondary text-center">
                {resources.length === 0
                  ? "No resources of this service for the current tenant."
                  : "No matches for the current filter."}
              </div>
            ) : (
              filteredResources.map((it) => {
                const key = composeKey(activeResourceType, it.id);
                const isAttached = attachedKeys.has(key);
                const isPicked = pickedIds.has(it.id);
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() =>
                      !isAttached && togglePicked(it.id)
                    }
                    disabled={isAttached}
                    className={`w-full grid ${isViewingAllCustomers ? "grid-cols-[36px_minmax(100px,1fr)_1fr_140px_120px]" : "grid-cols-[36px_1fr_140px_120px]"} gap-3 px-3 py-2 text-sm text-left items-center transition-colors ${
                      isAttached
                        ? "opacity-50 cursor-not-allowed"
                        : isPicked
                        ? "bg-semantic-state-active"
                        : "hover:bg-semantic-state-active"
                    }`}
                  >
                    <span className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isPicked || isAttached}
                        readOnly
                        disabled={isAttached}
                        className="h-4 w-4 cursor-pointer accent-semantic-bg-brand-bold-secondary"
                      />
                    </span>
                    {isViewingAllCustomers && (
                      <span className="min-w-0">
                        {it.customer ? (
                          <span className="flex flex-col">
                            <span className="font-medium text-semantic-text-primary truncate">
                              {it.customer.name}
                            </span>
                            {it.customer.chargebeeCustomerId && (
                              <span className="text-xs text-semantic-text-secondary truncate">
                                ID: {it.customer.chargebeeCustomerId}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-semantic-text-secondary">—</span>
                        )}
                      </span>
                    )}
                    <span className="text-semantic-text-primary truncate">
                      {it.label || `#${it.id}`}
                      {isAttached && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-semantic-bg-secondary text-semantic-text-secondary">
                          Already added
                        </span>
                      )}
                    </span>
                    <span className="text-semantic-text-secondary font-mono text-xs truncate">
                      {it.subscriptionId ?? "—"}
                    </span>
                    <span className="text-semantic-text-secondary text-xs truncate">
                      {it.status || "—"}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default GroupAssignmentsPicker;
